import React from 'react';
import { Callout, Kbd, List, P, Path, Steps, Sub, Table, UI, Only } from '../ui';

export const GettingStarted: React.FC = () => (
  <>
    <P>
      INA-TALK은 사내 인트라넷 계정으로 로그인해서 사용하는 업무용 메신저입니다. Windows PC에 설치하는 앱과,
      브라우저에서 바로 쓰는 웹 버전 두 가지가 있으며 대화 내용은 서로 완전히 동일하게 공유됩니다.
    </P>

    <Sub anchor="start-install">PC 버전 설치하기</Sub>
    <Steps>
      <>
        설치할 회사 PC에서 <b>인트라넷 자료실</b>에 접속해 INA-TALK 설치 파일을 내려받습니다.
      </>
      <>
        내려받은 설치 파일을 실행합니다. 설치 중 <b>관리자 권한을 묻는 창</b>이 뜨면 허용해 주세요.
      </>
      <>설치가 끝나면 바탕화면과 시작 메뉴에 INA-TALK 아이콘이 생깁니다.</>
      <>
        사내 인트라넷 계정으로 로그인합니다. 처음 로그인하면 이 PC가 <b>내 기기</b>로 등록됩니다 —
        회사 네트워크에서 처음 로그인하면 바로 쓸 수 있고, 회사 밖에서 처음 로그인하면 관리자 승인이 필요합니다.
      </>
    </Steps>

    <Callout tone="info" title="웹 브라우저 접속은 제한될 수 있습니다">
      <p>
        회사 보안 정책이 적용되면 웹 브라우저로는 로그인할 수 없고, <b>PC 앱</b>과 <b>관리자가 승인한 휴대폰</b>에서만
        쓸 수 있습니다. 자세한 내용은 <b>보안과 이용 수칙</b> 장의 &lsquo;기기 승인&rsquo;을 보세요.
      </p>
      <p>
        정책이 적용되기 전에는 PC의 웹 브라우저로 로그인한 뒤 <Path items={['왼쪽 아래 프로필 사진', 'PC 버전 다운로드']} />
        에서도 설치 파일을 받을 수 있습니다.
      </p>
    </Callout>

    <Callout tone="info" title="설치는 회사 네트워크에서만 됩니다">
      <p>
        설치 파일은 회사 네트워크에 연결된 PC에서만 설치됩니다. 집이나 외부에서 실행하면 &lsquo;회사
        네트워크에서만 설치할 수 있습니다&rsquo; 창이 뜨고 설치가 멈춥니다.
      </p>
      <p>이미 설치된 PC는 회사 밖에 있어도 업데이트를 자동으로 받습니다.</p>
    </Callout>

    <Callout tone="info" title="지원 환경">
      <p>Windows 10 / 11 (64비트)에서 사용할 수 있습니다. macOS·Linux용 설치 파일은 제공되지 않습니다.</p>
      <p>Mac을 쓰신다면 웹 브라우저로 접속해 사용하세요. 웹 브라우저 접속이 제한된 경우에는 관리자에게 문의해 주세요.</p>
    </Callout>

    <Sub anchor="start-login">로그인</Sub>
    <P>
      별도로 가입할 필요 없이 <b>사내 인트라넷에서 쓰던 아이디와 비밀번호</b>를 그대로 입력하면 됩니다.
    </P>
    <List>
      <>
        <b>계열사</b> — 접속 주소에 따라 계열사가 자동으로 정해지는 경우에는 선택 칸이 아예 표시되지 않습니다.
        여러 계열사를 고를 수 있는 환경에서만 맨 위에 드롭다운이 나타납니다.
      </>
      <>
        <b>인트라넷 아이디 / 비밀번호</b> — 두 칸 모두 입력해야 로그인 버튼이 동작합니다. 로그인 중에는 버튼이{' '}
        <UI>인증 중...</UI>으로 바뀝니다.
      </>
      <>
        아이디나 비밀번호가 틀리면 입력 칸 위에 빨간 오류 상자가 나타납니다. 인트라넷에서 비밀번호를 바꿨다면
        바뀐 비밀번호로 로그인하세요.
      </>
      <>
        <b>접근 권한</b> — 회사 정책에 따라 <b>승인된 계정만</b> 로그인할 수 있습니다. 아이디와 비밀번호가 맞는데도{' '}
        <UI>접근 권한이 없습니다</UI> 라고 나오면 관리자에게 승인을 요청하세요. 승인된 뒤에는 그대로 다시 로그인하면
        됩니다.
      </>
      <>
        <b>자동 로그인 / 아이디 저장</b> — 로그인 버튼 위의 체크박스입니다. PC 앱에서는 <UI>자동 로그인</UI>으로,
        켜 두면 아이디와 비밀번호가 이 PC의 내 Windows 계정에만 풀리는 형태로 암호화 저장되어 다음부터는
        로그인 화면을 거치지 않습니다. 웹 브라우저에서는 <UI>아이디 저장</UI>으로, 아이디만 기억하고 비밀번호는
        브라우저의 비밀번호 저장 기능에 맡깁니다.
      </>
    </List>

    <Callout tone="tip" title="한 번 로그인하면 다음부터는 자동으로 열립니다">
      <p>
        로그인 상태는 앱을 껐다 켜도 유지되어 로그인 화면을 거치지 않고 바로 대화 화면이 열리며,{' '}
        <b>마지막에 보고 있던 대화방까지 그대로 복원</b>됩니다.
      </p>
      <p>
        보안을 위해 일정 기간이 지나면 로그인이 만료됩니다. PC 앱에서 <UI>자동 로그인</UI>을 켜 두었다면 이때도
        저장된 정보로 조용히 다시 로그인하고, 켜 두지 않았다면{' '}
        <UI>세션이 만료되었습니다. 다시 로그인해주세요.</UI> 안내가 뜨고 로그인 화면으로 돌아갑니다.
      </p>
      <p>
        내가 직접 <UI>로그아웃</UI>하면 자동 로그인은 꺼집니다(아이디만 채워진 채 로그인 화면이 뜹니다). 다시
        체크하고 로그인하면 자동 로그인이 다시 켜집니다.
      </p>
    </Callout>

    <Sub anchor="start-consent">처음 로그인하면 나오는 이용 고지</Sub>
    <P>
      처음 로그인할 때 <b>INA-TALK 이용 고지 및 동의</b> 창이 표시됩니다. 메시지 보안과 금지 사항 등에 대한
      안내이며, 아래쪽 항목을 <b>하나씩</b> 체크해야 <UI>동의 후 시작</UI> 버튼을 누를 수 있습니다. 바깥쪽을
      클릭해서는 닫히지 않고, <UI>동의하지 않음</UI>을 누르면 로그아웃됩니다.
    </P>
    <P>
      동의는 계정 단위로 기록됩니다. 한 기기에서 동의하면 PC와 휴대폰 모두 <b>100일 동안 다시 뜨지 않습니다.</b>{' '}
      100일이 지나거나 고지 내용이 바뀌면 다시 표시됩니다. 내용과 내 동의 내역은{' '}
      <UI>왼쪽 아래 프로필 사진 &rarr; 이용 고지·동의</UI>에서 언제든 볼 수 있고, 모바일에서는{' '}
      <UI>설정 탭 &rarr; 이용 고지·동의</UI>에서 열립니다. 같은 화면에서 동의를 철회할 수 있으며, 철회하면 모든
      기기에서 로그아웃됩니다.
    </P>

    <Sub anchor="start-layout">화면 구성</Sub>
    <P>대화 화면은 크게 네 부분으로 나뉩니다.</P>
    <Table
      head={['영역', '위치', '하는 일']}
      rows={[
        [
          <>
            제목 표시줄 <Only env="desktop" />
          </>,
          '맨 위',
          '창 이동, 최소화, 최대화, 닫기 버튼',
        ],
        [
          '탭 바',
          '왼쪽 끝(세로)',
          '홈·대화방·캘린더·알림·온라인 전환 버튼. 아래쪽에 관리자·도움말 버튼과 내 프로필 사진이 있고, 대화방 버튼에 안 읽은 개수가 표시됩니다',
        ],
        [
          '목록 패널',
          '탭 바 오른쪽',
          '선택한 탭의 내용 — 대화방 목록, 알림함, 온라인 목록이 여기에 표시됩니다',
        ],
        [
          '대화창',
          '오른쪽(가장 넓음)',
          '메시지를 읽고 쓰는 공간. 위쪽 머리글에 통화·멤버·검색·닫기 버튼이 있습니다. 홈에서 대화방 탭으로 돌아오면 마지막으로 보던 대화가 다시 열립니다',
        ],
      ]}
    />
    <P>
      <UI>홈</UI> 탭은 목록 패널과 대화창 대신 <b>홈 화면이 넓게 펼쳐집니다</b>. 왼쪽 단에는 <b>확인할 것</b>(안 읽은
      메시지·알림함 개수)과 <b>최근 대화</b>, 오른쪽 단에는 <b>일정(이번 달 달력과 오늘 일정)·공지·즐겨찾기·지금
      대화할 수 있는 동료</b>와 기능 팁이 있습니다. 대화방을 누르면 대화방 탭으로 바뀌며 오른쪽에 대화가 열립니다.{' '}
      <UI>캘린더</UI> 탭도 홈처럼 캘린더가 넓게 펼쳐집니다.
    </P>

    <Callout tone="info" title="창이 좁아지면 배치가 바뀝니다">
      <p>
        <b>PC 앱</b>은 카카오톡 PC처럼 창을 좁게 줄여 쓸 수 있습니다. 좁아지면 왼쪽 탭 바는 그대로 두고 목록 한 칸만
        보이며, 대화방을 열면 목록 자리에 대화가 열립니다. 왼쪽 위 <Kbd>&lt;</Kbd> 버튼이나 왼쪽 탭 바를 누르면 목록으로
        돌아갑니다. 줄인 창 크기와 위치는 다음에 앱을 켤 때도 그대로입니다.
      </p>
      <p>
        <b>웹 브라우저·휴대폰</b>에서는 화면 아래에 <UI>홈</UI>·<UI>대화방</UI>·<UI>캘린더</UI>·<UI>온라인</UI>·<UI>설정</UI>
        탭바가 있는 모바일 배치로 바뀝니다. 대화방을 열면 전체 화면으로 전환되고, 왼쪽 위 <Kbd>&lt;</Kbd>
        버튼으로 목록에 돌아갑니다. 홈 화면 오른쪽 아래 <UI>새 대화</UI> 버튼으로 바로 대화방을 개설할 수 있습니다.
      </p>
    </Callout>

    <Sub>가장 먼저 해두면 좋은 것</Sub>
    <List>
      <>
        <b>프로필 사진 정하기</b> — <Path items={['왼쪽 탭 바 맨 아래 내 사진', '프로필 설정']} /> 에서
        아바타를 고릅니다.
      </>
      <>
        <b>알림이 잘 오는지 확인</b> — 화면 위쪽에 노란색 알림 안내 배너가 보인다면 Windows 알림이 꺼져 있는
        상태입니다. 배너의 버튼으로 바로 설정을 열 수 있습니다.
      </>
      <>
        <b>동료 찾아보기</b> — <UI>온라인</UI> 탭에서 회사 → 부서를 펼쳐 동료를 누르면 프로필이 열립니다.
        프로필의 <UI>1:1 대화</UI> 버튼으로 바로 대화를 시작할 수 있습니다.
      </>
    </List>
  </>
);
